import type { AriaAttributes, AriaRole, CSSProperties, ReactNode } from 'react';

export type { IconList, SvgIcons, SvgImages, SvgLogos, SvgTenants } from '@snowit/assets';

export const COLOR = [
  'primary',
  'text',
  'text-l',
  'text-inverse',
  'text-inverse-l',
  'primary-l',
  'primary-l2',
  'bg',
  'bg-d',
  'bg-d2',
  'bg-d3',
  'bg-d4',
  'ok',
  'warning',
  'error',
  'info',
  'ok-l',
  'warning-l',
  'error-l',
  'info-l',
  'gold',
  'silver',
  'bronze',
  'white',
  'black',
  'blue',
  'blue-l',
  'brown',
  'brown-l',
  'cyan',
  'cyan-l',
  'deep-orange',
  'deep-orange-l',
  'deep-purple',
  'deep-purple-l',
  'indigo',
  'indigo-l',
  'light-blue',
  'light-blue-l',
  'light-green',
  'light-green-l',
  'lime',
  'lime-l',
  'orange',
  'orange-l',
  'pink',
  'pink-l',
  'purple',
  'purple-l',
  'teal',
  'teal-l',
] as const;

/** @deprecated Use COLOR instead. */
export const ICONCOLOR = COLOR;

export const CSSCOLORS = {
  blue: 'var(--c-blue)',
  lightBlue: 'var(--c-light-blue)',
  brown: 'var(--c-brown)',
  cyan: 'var(--c-cyan)',
  orange: 'var(--c-orange)',
  deepOrange: 'var(--c-deep-orange)',
  indigo: 'var(--c-indigo)',
  pink: 'var(--c-pink)',
  lightGreen: 'var(--c-light-green)',
  lime: 'var(--c-lime)',
  purple: 'var(--c-purple)',
  deepPurple: 'var(--c-deep-purple)',
  teal: 'var(--c-teal)',
  gray: 'var(--c-text)',
  lightGray: 'var(--c-text-l)',
} as const;

export type CssColors = typeof CSSCOLORS;

export type StringOrNode = string | ReactNode | (() => string | ReactNode);
export type IceFunction = (...args: any[]) => void;

export const SIZE = [
  'qr',
  'tr',
  'big',
  'db',
  'xxl',
  'xl',
  'lg',
  'df',
  'sm',
  'xs',
  'xxs',
  'uxs',
] as const;

export type Size = (typeof SIZE)[number];

export type Color = (typeof COLOR)[number];
export type IconPosition = 'before' | 'after';
export type Alignment = 'left' | 'center' | 'right';
export type Tag = 'div' | 'footer' | 'header' | 'section' | 'article' | 'aside';

// A11y
export type A11y = {
  role?: AriaRole;
  label?: AriaAttributes['aria-label'];
  hidden?: AriaAttributes['aria-hidden'];
  expanded?: AriaAttributes['aria-expanded'];
  controls?: AriaAttributes['aria-controls'];
  haspopup?: AriaAttributes['aria-haspopup'];
  orientation?: AriaAttributes['aria-orientation'];
  describedBy?: AriaAttributes['aria-describedby'];
  htmlFor?: string;
  tabIndex?: number;
};

// Images
export type LoaderImageType =
  | 'cabins'
  | 'cartAdd'
  | 'hotelSearch'
  | 'bike'
  | 'kitesurf'
  | 'jeep'
  | 'snowcat'
  | 'gears'
  | 'train'
  | 'bus'
  | 'ferry'
  | 'cervino';
export type ErrorImageType = 'snowman' | 'kitesurf' | 'bike' | 'train';

// Accordion
export type AccordionTheme = 'default' | 'box' | 'line';

// BadgeLabel
export type BadgeLabelSize = Extract<Size, 'df' | 'sm' | 'xs'>;
export type BadgeLabelTheme =
  | 'color'
  | 'square'
  | 'squareOutline'
  | 'outline'
  | 'rounded'
  | 'roundedOutline';
export type BadgeLabelVariant =
  | 'gradient'
  | 'primary'
  | 'secondary'
  | 'ok'
  | 'info'
  | 'warning'
  | 'error'
  | 'ok_fill'
  | 'error_fill'
  | 'info_fill'
  | 'warning_fill';

// Banner
export type BannerSize = Extract<Size, 'df' | 'sm' | 'xs'>;
export type BannerTheme =
  | 'error'
  | 'errorText'
  | 'info'
  | 'infoBox'
  | 'infoText'
  | 'ok'
  | 'okText'
  | 'tips'
  | 'warning'
  | 'warningText';

export type BannerSizeV2 = Extract<Size, 'db' | 'xl' | 'sm'>;
export type BannerThemeV2 = 'error' | 'info' | 'ok' | 'tips' | 'warning' | 'neutral';

// BannerIllustration
export type BannerIllustrationTheme = 'default' | 'box' | 'fill';

// Button
export type ButtonShape = 'default' | 'pill';
export type ButtonTheme = 'fill' | 'outline' | 'ghost' | 'text';

/** TODO: Extract<Size, 'xl' | 'xxl' | 'db' | 'big' | 'tr' | 'qr'> */
export type ButtonSize = Extract<
  Size,
  'df' | 'lg' | 'sm' | 'xs' | 'xl' | 'xxl' | 'db' | 'big' | 'tr' | 'qr'
>;

// ButtonIcon
export type ButtonIconSize = Extract<Size, 'qr' | 'tr' | 'big' | 'db' | 'xxl'>;
export type ButtonIconTheme = 'fill' | 'outline' | 'ghost' | 'transparent';
export type ButtonIconColor = Color;

// ButtonMenu
export type ButtonMenuTheme = 'icon' | 'user';

// Checkbox
export type CheckboxTheme = 'default' | 'background' | 'box' | 'boxBackground';

// Divider
export type DividerTheme =
  | 'bg'
  | 'bgFull'
  | 'fullToLine'
  | 'line'
  | 'verticalLine'
  | 'text'
  | 'space';
export type DividerSpacing = number | number[];

// Flex
export type FlexTag = Tag;
export type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';
export type FlexJustify = 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
export type FlexAlign = 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
export type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

// GalleryModal
export type GalleryModalTheme = 'default' | 'light';

// Icon
export type IconColor = Color;

// Img
export type ImgFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';

// Input
export type InputSize = Extract<Size, 'tr' | 'db'>;

export type InputType = 'text' | 'email' | 'url' | 'tel' | 'password' | 'number';

// InputSpinner
export type InputSpinnerTheme = 'default' | 'box';

// LoaderSpinner
export type LoaderSpinnerTheme = 'bars' | 'circle' | 'hexagon';

// Price
export type PriceTheme = 'vertical' | 'horizontal' | 'option';
export type PriceAlignment = Exclude<Alignment, 'center'>;

// RadioButton
export type RadioButtonTheme = 'default' | 'box';

// RadioCard
export type RadioCardTheme = 'default' | 'slot' | 'slotFilter' | 'product';

// RadioSwitch
export type RadioSwitchTheme = 'default' | 'lang' | 'basic';

// Rating
export type RatingTheme = 'dot' | 'stars' | 'starsNumber';

// Status
export type StatusTheme = 'icon' | 'badge' | 'label';
export type StatusThemeV2 = 'icon' | 'badge' | 'badgeIcon' | 'label';

export type StatusType =
  | 'last'
  | 'error'
  | 'rejected'
  | 'few'
  | 'warning'
  | 'processing'
  | 'pending'
  | 'available'
  | 'ok'
  | 'worn';

// Typo
export type TypoTag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'label' | 'span' | 'kbd';
export type TypoType =
  | 'hJumbo'
  | 'hBIG'
  | 'hDB'
  | 'hXXL'
  | 'hXL'
  | 'hLG'
  | 'hDF'
  | 'hMD'
  | 'hSM'
  | 'hXS'
  | 'hXXS'
  | 'tJumbo'
  | 'tBIG'
  | 'tDB'
  | 'tXXL'
  | 'tXL'
  | 'tLG'
  | 'tDF'
  | 'tMD'
  | 'tSM'
  | 'tXS'
  | 'tXXS';
export type TypoFamily = 'default' | 'mono';
export type TypoSize =
  | 'qt'
  | 'qr'
  | 'tr'
  | 'big'
  | 'db'
  | 'xxl'
  | 'xl'
  | 'lg'
  | 'df'
  | 'md'
  | 'sm'
  | 'xs'
  | 'xxs'
  /** @deprecated use tr instead */
  | 'xbig';

export type TypoWeight =
  | 'light'
  | 'regular'
  | 'semi-bold'
  | 'bold'
  | 'extra-bold'
  /** @deprecated use semi-bold instead */
  | 'medium';
export type TypoLineHeight = 'default' | 'paragraph';
export type TypoColor = Color;

export type BreakpointEnum = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type BreakpointDeviceEnum = 'mobile' | 'tabletL' | 'tabletP' | 'desktop';

export type CSSKeys = keyof CSSProperties | (string & {});
export type CSSValues = string | number | undefined | null;
export type CSSMakeFn = (input: { [x in CSSKeys]?: CSSValues }) => CSSProperties;
